import { createSelector } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { RootState } from "app/store";
import { MatchType } from "../types/matchType";
import { AppFeature } from "../types/AppFeature";
import { MsalInstanceService } from "features/auth/MsalInstanceService";

const selectHasSuccessfullyFetchedFeatures = (state: RootState) =>
    state.appFeatures.hasSuccessfullyFetchedFeatures;

const selectMyEnabledFeatures = (
    state: RootState,
): Record<AppFeature, string | undefined> => state.appFeatures.enabledFeatures;

const selectEnabledFeaturesErrorResponse = (state: RootState) =>
    state.appFeatures.enabledFeaturesErrorResponse;

const selectFeaturesDebugData = createSelector(
    [
        selectMyEnabledFeatures,
        selectEnabledFeaturesErrorResponse,
        (state: RootState, data: object | undefined) => data,
    ],
    (
        enabledFeatures: Record<AppFeature, string | undefined>,
        errorResponse: FetchBaseQueryError | undefined,
        data: object | undefined,
    ) => {
        const userName = MsalInstanceService.getInstance().getCurrentUserName();

        const debugData = {
            userName,
            enabledFeatures,
            errorResponse,
            data,
        };

        return btoa(JSON.stringify(debugData));
    },
);

const selectUserHasFeatures = createSelector(
    [
        selectMyEnabledFeatures,
        (state: RootState, requestedFeatures: AppFeature[]) =>
            requestedFeatures,
        (
            state: RootState,
            requestedFeatures: AppFeature[],
            matchType?: MatchType,
        ) => matchType,
    ],
    (
        enabledFeatures: Record<AppFeature, string | undefined>,
        requestedFeatures: AppFeature[],
        matchType?: MatchType,
    ) => {
        if (!matchType || matchType === MatchType.All) {
            return requestedFeatures.every(
                (feature) => feature in enabledFeatures,
            );
        }

        return requestedFeatures.some((feature) => feature in enabledFeatures);
    },
);

const selectFeatureVariant = createSelector(
    [
        selectMyEnabledFeatures,
        (state: RootState, requestedFeature: AppFeature) => requestedFeature,
    ],
    (
        enabledFeatures: Record<AppFeature, string | undefined>,
        requestedFeature: AppFeature,
    ) => {
        const variant = enabledFeatures[requestedFeature];

        return variant ? JSON.parse(variant) : undefined;
    },
);

export const appFeaturesSelectors = {
    selectHasSuccessfullyFetchedFeatures,
    selectMyEnabledFeatures,
    selectEnabledFeaturesErrorResponse,
    selectFeaturesDebugData,
    selectUserHasFeatures,
    selectFeatureVariant,
};
